<template>
  <div class="musubi-page musubi-page--analytics">
    <header class="musubi-page__head">
      <h1>Analytics</h1>
      <p class="text-muted">Acquisition &amp; outreach attribution — sessions through to attributed subscribers.</p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <!-- No first-party sessions yet → nudge to install the tracking snippet. -->
    <a v-if="!loading && s.sessions.total === 0" class="an-setup" href="/admin/tracking">
      <CygnusIcon name="broadcast" />
      <div>
        <b>Set up tracking</b>
        <span>No visitor sessions yet — install the first-party pixel to start measuring acquisition.</span>
      </div>
      <CygnusIcon name="chevron-right" class="an-setup__chev" />
    </a>

    <!-- KPIs -->
    <div class="an-kpis">
      <MusubiKpiCard label="Sessions" :value="fmt(s.sessions.total)" icon="chart-line" />
      <MusubiKpiCard label="Identified" :value="fmt(s.sessions.identified)" :delta="identifiedPct + '% of sessions'" icon="user-check" />
      <MusubiKpiCard label="Subscribers" :value="fmt(s.subscribers.total)" icon="users" />
      <MusubiKpiCard label="Attributed" :value="fmt(s.subscribers.attributed)" :delta="attributedPct + '% of subs'" icon="diagram" />
    </div>

    <div class="an-grid">
      <!-- Acquisition funnel -->
      <MusubiSectionCard title="Acquisition funnel" subtitle="first-touch, all time" icon="filter">
        <div v-if="loading" class="text-muted small py-3">Loading…</div>
        <MusubiPyramidFunnel v-else :stages="funnel" />
      </MusubiSectionCard>

      <!-- Engagement events + quick links -->
      <div class="an-side">
        <MusubiSectionCard title="Engagement" icon="bolt">
          <div class="an-events">
            <div class="an-event"><span class="an-event__n">{{ fmt(s.events.pageview) }}</span><span class="an-event__l">Pageviews</span></div>
            <div class="an-event"><span class="an-event__n">{{ fmt(s.events.email_open) }}</span><span class="an-event__l">Email opens</span></div>
            <div class="an-event"><span class="an-event__n">{{ fmt(s.events.email_click) }}</span><span class="an-event__l">Email clicks</span></div>
          </div>
        </MusubiSectionCard>

        <MusubiSectionCard title="Explore" icon="route">
          <a class="an-link" href="/analytics/attribution"><CygnusIcon name="diagram" /><div><b>Attribution</b><span>By source &amp; campaign</span></div><CygnusIcon name="chevron-right" class="an-chev" /></a>
          <a class="an-link is-soon"><CygnusIcon name="filter" /><div><b>Conversion funnels</b><span>Signup · trial · purchase</span></div><span class="an-soon">soon</span></a>
          <a class="an-link is-soon"><CygnusIcon name="chart-bar" /><div><b>Per-customer reporting</b><span>Individual account dashboards</span></div><span class="an-soon">soon</span></a>
          <a class="an-link is-soon"><CygnusIcon name="pin" /><div><b>Regional demand map</b><span>By territory &amp; provider</span></div><span class="an-soon">soon</span></a>
        </MusubiSectionCard>
      </div>
    </div>

    <!-- Prospect → outreach funnel (acquisition lifecycle) -->
    <MusubiSectionCard title="Prospect → outreach funnel" subtitle="discovered → promoted, all time" icon="chevron-down" class="an-acq-funnel">
      <div v-if="loading" class="text-muted small py-3">Loading…</div>
      <MusubiPyramidFunnel v-else-if="acqFunnel.length" :stages="acqFunnel" />
      <p v-else class="text-muted small py-3">No prospects yet.</p>
    </MusubiSectionCard>

    <!-- Top sources -->
    <MusubiSectionCard title="Top sources" subtitle="sessions → conversions" icon="broadcast" class="an-sources">
      <table v-if="s.by_source.length" class="an-table">
        <thead><tr><th>Source</th><th>Sessions</th><th>Conversions</th><th>Rate</th></tr></thead>
        <tbody>
          <tr v-for="(r, i) in s.by_source.slice(0, 8)" :key="i">
            <td class="an-src">{{ r.value }}</td>
            <td>{{ fmt(r.sessions) }}</td>
            <td>{{ fmt(r.conversions) }}</td>
            <td><span class="an-rate">{{ rate(r) }}%</span></td>
          </tr>
        </tbody>
      </table>
      <p v-else class="text-muted small py-3">No session data yet.</p>
    </MusubiSectionCard>
  </div>
</template>

<script>
/**
 * AdminAnalytics — Part 3 (Analytics) section landing. Composes the design-
 * system primitives over live /api/v1/admin/attribution/summary, headlined by
 * MusubiPyramidFunnel (sessions → identified → subscribers → attributed).
 * Event funnels / client hub / heatmap are flagged as upcoming.
 */
import useApi from "@apexl/icarus-core/src/services/api";

const EMPTY = {
  sessions: { total: 0, identified: 0 },
  subscribers: { total: 0, attributed: 0 },
  events: { pageview: 0, email_open: 0, email_click: 0 },
  by_source: [], by_campaign: [],
};

export default {
  name: 'AdminAnalytics',
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return { api: useApi(), s: EMPTY, acqStages: [], loading: false, error: null };
  },
  mounted() { this.load(); },
  computed: {
    identifiedPct() {
      return this.s.sessions.total ? Math.round((this.s.sessions.identified / this.s.sessions.total) * 100) : 0;
    },
    attributedPct() {
      return this.s.subscribers.total ? Math.round((this.s.subscribers.attributed / this.s.subscribers.total) * 100) : 0;
    },
    funnel() {
      const s = this.s;
      return [
        { label: 'Sessions',    count: s.sessions.total },
        { label: 'Identified',  count: s.sessions.identified },
        { label: 'Subscribers', count: s.subscribers.total },
        { label: 'Attributed',  count: s.subscribers.attributed },
      ];
    },
    acqFunnel() {
      return this.acqStages.map((st) => ({ label: st.label, count: st.count }));
    },
  },
  methods: {
    async load() {
      this.loading = true; this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/attribution/summary');
        const p = res?.data?.data ?? res?.data ?? res;
        this.s = { ...EMPTY, ...(p || {}) };
        this.s.sessions = { ...EMPTY.sessions, ...(p?.sessions || {}) };
        this.s.subscribers = { ...EMPTY.subscribers, ...(p?.subscribers || {}) };
        this.s.events = { ...EMPTY.events, ...(p?.events || {}) };
        this.s.by_source = p?.by_source || [];
      } catch (e) {
        this.error = e?.message || 'Failed to load analytics.';
      }
      try {
        const fr = await this.api.get('/api/v1/admin/leads/funnel');
        this.acqStages = (fr?.data ?? fr)?.stages || [];
      } catch (e) { /* non-fatal */ }
      this.loading = false;
    },
    fmt(n) { return (Number(n) || 0).toLocaleString(); },
    rate(r) { return r.sessions ? Math.round((r.conversions / r.sessions) * 100) : 0; },
  },
};
</script>

<style scoped>
.an-setup { display: flex; align-items: center; gap: 12px; margin: 16px 0 4px; padding: 13px 16px; border: 1px solid var(--m-accent); border-radius: 10px; background: var(--m-accent-soft, rgba(79,70,229,0.07)); text-decoration: none; color: var(--m-text-primary); }
.an-setup:hover { background: var(--m-accent-soft, rgba(79,70,229,0.12)); }
.an-setup > i:first-child { color: var(--m-accent); font-size: 1.2rem; }
.an-setup b { display: block; font-size: 0.9rem; }
.an-setup span { font-size: 0.78rem; color: var(--m-text-secondary); }
.an-setup__chev { margin-left: auto; color: var(--m-text-tertiary); }
.an-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 18px 0; }
.an-grid { display: grid; grid-template-columns: 1.4fr 1fr; gap: 16px; align-items: start; }
.an-side { display: grid; gap: 16px; }
.an-events { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.an-event { text-align: center; padding: 8px 4px; }
.an-event__n { display: block; font-size: var(--m-text-xl); font-weight: 750; color: var(--m-text-primary); }
.an-event__l { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--m-text-tertiary); }
.an-sources { margin-top: 16px; }
.an-table { width: 100%; border-collapse: collapse; }
.an-table th { text-align: left; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--m-text-tertiary); font-weight: 600; padding: 8px 6px; border-bottom: 1px solid var(--m-border); }
.an-table td { padding: 10px 6px; border-bottom: 1px solid var(--m-border); }
.an-src { font-weight: 650; }
.an-rate { font-weight: 700; color: var(--m-accent); }
.an-link { display: flex; align-items: center; gap: 12px; padding: 11px 8px; border-bottom: 1px solid var(--m-border); text-decoration: none; color: var(--m-text-primary); }
.an-link:last-child { border-bottom: none; }
.an-link:not(.is-soon):hover { color: var(--m-accent); }
.an-link.is-soon { opacity: 0.62; cursor: default; }
.an-link > i:first-child { color: var(--m-accent); width: 20px; text-align: center; }
.an-link b { display: block; font-size: 0.88rem; }
.an-link span { color: var(--m-text-tertiary); font-size: 0.75rem; }
.an-chev { margin-left: auto; color: var(--m-text-tertiary); }
.an-soon { margin-left: auto; font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-tertiary); font-weight: 600; }
@media (max-width: 1024px) { .an-kpis { grid-template-columns: repeat(2, 1fr); } .an-grid { grid-template-columns: 1fr; } .an-events { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 640px)  { .an-kpis { grid-template-columns: 1fr; } .an-events { grid-template-columns: 1fr; } }
</style>
